import * as React from "react";
import { ObjectO2 } from "src/pages/ManagementPage/Obj2Tab/Obj2Nodes";
import {
  SelectDictionary,
  SelectDictionaryStore,
} from "src/pages/ManagementPage/Obj2Tab/forms/AttrForm2/SelectDictionary";
import { Form, Radio, Select, Switch } from "antd";
import { ZUid } from "src/types/zUid";
import { onError } from "src/common/onError";
import { AttrTypeName } from "src/types/AttrType";
import { loadObjectAttrinbutesAll } from "src/pages/ManagementPage/objectsApi";
import { DefaultOptionType } from "antd/lib/select";
import { makeFieldOptions } from "src/common/makeFieldOptions";
import { Bom3MatrixEdit } from "./Bom3MatrixEdit";
import { loadBom3Matrix, loadBom3ProductCategoryMatrix } from "../apiBom3";
import { ZBom3MatrixSettings, ZBom3Settings } from "../ZBom3Settings";
import { BsObjectSection } from "../../../BsObjectSection";
import styles from "./Bom3Settings.module.less";

type PropsBom3Settings = {
  modelObjectId: ZUid;
  typesMap: Record<number, string>;
  objList: ObjectO2[];
};

const fk = (key: keyof ZBom3Settings): string => key;

export const Bom3Settings: React.FC<PropsBom3Settings> = (props) => {
  const { modelObjectId, typesMap, objList } = props;
  const dictStore1 = React.useMemo(() => new SelectDictionaryStore(), []);
  const dictStore2 = React.useMemo(() => new SelectDictionaryStore(), []);
  const numExtTypes = React.useMemo(
    () =>
      new Set<string | undefined>([
        AttrTypeName.int,
        AttrTypeName.number,
        AttrTypeName.formula,
      ]),
    [],
  );

  const form = Form.useFormInstance<ZBom3Settings>();
  const sizeVariations: boolean = Form.useWatch(fk("sizeVariations"));
  const commonSumma: boolean = Form.useWatch(fk("commonSumma"));
  const consumptionBySize: boolean = Form.useWatch(fk("consumptionBySize"));

  const [matrixLoading, onMatrixChange] = useMatrixLoading({
    async load() {
      const bomSectionId: ZUid | undefined = form.getFieldValue(
        fk("dictBomSection"),
      );
      const materialTypeId: ZUid | undefined = form.getFieldValue(
        fk("dictMaterialType"),
      );
      if (!bomSectionId || !materialTypeId) return null;
      return loadBom3Matrix(bomSectionId, materialTypeId);
    },
    onSuccess(matrix) {
      if (matrix) {
        form.setFieldValue(fk("directoryMatrix"), matrix);
      }
    },
  });

  const [pcMatrixLoading, onPcMatrixChange] = useMatrixLoading({
    async load() {
      const bomSectionId: ZUid | undefined = form.getFieldValue(
        fk("dictBomSection"),
      );
      const productCategoryId: ZUid | undefined = form.getFieldValue(
        fk("attModelDictProductCategory"),
      );
      if (!bomSectionId || !productCategoryId) return null;
      return loadBom3ProductCategoryMatrix(bomSectionId, productCategoryId);
    },
    onSuccess(matrix) {
      if (matrix) {
        form.setFieldValue(fk("productCategoryMatrix"), matrix);
      }
    },
  });

  // Только для типа «размер материала»: объект Material size; связь Material и Material size;
  // атрибут Size variation на BOM position.
  const materialSizeType = !consumptionBySize;

  const [modelDictAttrs, setModelDictAttrs] = React.useState<
    DefaultOptionType[]
  >([]);
  const [modelAttrsLoading, setModelAttrsLoading] = React.useState(false);
  React.useEffect(() => {
    const loadModelAttrs = async () => {
      const list = await loadObjectAttrinbutesAll(modelObjectId);
      const dictList = list.filter(
        ({ valueType }) => typesMap[valueType] === AttrTypeName.dictSingle,
      );
      setModelDictAttrs(makeFieldOptions(dictList));
    };
    setModelAttrsLoading(true);
    loadModelAttrs()
      .finally(() => setModelAttrsLoading(false))
      .catch(onError);
  }, [modelObjectId]);

  return (
    <div className={styles.box}>
      <h3>BOM и BOM position</h3>
      <BsObjectSection
        objLabel="Объект BOM"
        objName={fk("objBom")}
        objList={objList}
        attrs={[
          {
            attrName: fk("attLinkBomToSizeLine"),
            label: "Связь BOM и Size line",
            hidden: !sizeVariations,
          },
          {
            attrName: fk("attLinkBomToModel"),
            label: "Атрибут Model в BOM",
            hidden: !sizeVariations,
          },
          {
            attrName: fk("attBomTotalAmount"),
            label: "Атрибут общей суммы в BOM",
            hidden: !commonSumma,
            // LPLM-5413 Для "Атрибут общей суммы в BOM" возможен выбор только атрибутов с типом "дробное число"
            attrValid: ({ valueType }) =>
              typesMap[valueType] === AttrTypeName.number,
          },
        ]}
      />
      <BsObjectSection
        objLabel="Объект BOM position"
        objName={fk("objBomPosition")}
        objList={objList}
        attrs={[
          {
            attrName: fk("attBomPositionLinkToMaterial"),
            label: "Связь Material-BOM position",
          },
          {
            attrName: fk("attBomPositionColorLinks"),
            label: "Атрибут Color Links на BOM position",
          },
          {
            attrName: fk("attLinkBomPositionToSizeVariation"),
            label: "Связь BOM position и размерной вариации",
            hidden: !sizeVariations, // || !materialSizeType, - в требованиях это было написано. но устно отменили. см LPLM-5827
          },
          {
            attrName: fk("attBomPositionAmount"),
            label: "Атрибут для расчета в BOM position",
            hidden: !commonSumma,
            // LPLM-5413 Для "Атрибут для расчета в BOM position" возможен выбор атрибутов с типом "Дробное число", "Целое число" и "Формула"
            attrValid: ({ valueType }) => numExtTypes.has(typesMap[valueType]),
          },
        ]}
      />

      <h3>Material</h3>
      <BsObjectSection
        objLabel="Объект Material"
        objName={fk("objMaterial")}
        objList={objList}
        attrs={[
          {
            label: "Атрибут Material Type на Material",
            attrName: fk("attMaterialType"),
          },
        ]}
      />

      <h3>Color и ColorModel</h3>
      <BsObjectSection
        objLabel="Объект Color"
        objName={fk("objColor")}
        objList={objList}
      />
      <BsObjectSection
        objLabel="Объект ColorModel"
        objName={fk("objColorModel")}
        objList={objList}
        attrs={[
          {
            label: "Связь Model-ColorModel",
            attrName: fk("attColorLinkToModel"),
          },
        ]}
      />

      <h3>Секции ВОМ и категории</h3>
      <Form.Item
        label="Категории товаров"
        name={fk("attModelDictProductCategory")}
        rules={[
          {
            required: true,
            message:
              "Необходимо выбрать атрибут - справочник категорий товаров",
          },
        ]}
      >
        <Select
          options={modelDictAttrs}
          allowClear
          loading={modelAttrsLoading}
          onChange={() => {
            onPcMatrixChange();
          }}
        />
      </Form.Item>
      <Form.Item
        label="Секции ВОМ"
        name={fk("dictBomSection")}
        rules={[{ required: true }]}
      >
        <SelectDictionary
          store={dictStore1}
          onChange={() => {
            onMatrixChange();
            onPcMatrixChange();
          }}
        />
      </Form.Item>
      <Form.Item
        label="Типы материалов"
        name={fk("dictMaterialType")}
        rules={[{ required: true }]}
      >
        <SelectDictionary store={dictStore2} onChange={onMatrixChange} />
      </Form.Item>

      <Bom3MatrixEdit
        label="Секции ВОМ + Типы материалов"
        loading={matrixLoading}
        name="directoryMatrix"
      />

      <Bom3MatrixEdit
        label="Категории товаров + Секции ВОМ"
        loading={pcMatrixLoading}
        name="productCategoryMatrix"
      />

      {/* ---------- Размерные вариации -----------  */}
      <BomSwitch name="sizeVariations" label="Размерные вариации" />

      {sizeVariations !== false && (
        <>
          <h3>Size line и Size name</h3>

          <BsObjectSection
            objLabel="Объект Size line"
            objName={fk("objSizeLine")}
            objList={objList}
          />

          <BsObjectSection
            objLabel="Объект Size name"
            objName={fk("objSizeName")}
            objList={objList}
            attrs={[
              {
                label: "Связь Size line и Size name",
                attrName: fk("attLinkSizeNameToSizeLine"),
              },
            ]}
          />

          {materialSizeType && (
            <>
              <h3>Material size</h3>

              <BsObjectSection
                objLabel="Объект Material size"
                objName={fk("objMaterialSize")}
                objList={objList}
                attrs={[
                  {
                    label: "Связь Material и Material size",
                    attrName: fk("attLinkMaterialSizeToMaterial"),
                  },
                ]}
              />
            </>
          )}

          <Form.Item
            name={fk("consumptionBySize")}
            label="Тип размерных вариаций"
          >
            <Radio.Group
              options={[
                { value: false, label: "Размер изделия → размер материала" },
                { value: true, label: "Размер изделия → реальный расход" },
              ]}
              defaultValue={false}
              optionType="button"
            />
          </Form.Item>
        </>
      )}

      {/* ---------- сумма строк БОМ ---------------- */}

      <BomSwitch name="commonSumma" label="Общая сумма BOM" />
    </div>
  );
};

type PropsBomSwitch = {
  name: keyof ZBom3Settings;
  label: string;
};

const BomSwitch: React.FC<PropsBomSwitch> = ({ name, label }) => (
  <div className={styles.labelBox}>
    <label>
      <Form.Item noStyle name={name}>
        <Switch />
      </Form.Item>
      <div>{label}</div>
    </label>
  </div>
);

const useMatrixLoading = (params: {
  load: () => Promise<ZBom3MatrixSettings | null>;
  onSuccess: (matrix: ZBom3MatrixSettings | null) => void;
}): [boolean, () => void] => {
  const { load, onSuccess } = params;
  const counter = React.useRef(0);
  const [matrixLoading, setMatrixLoading] = React.useState(false);

  const onMatrixChange = () => {
    counter.current += 1;
    const localCounter = counter.current;
    setMatrixLoading(true);
    load()
      .then((matrix) => {
        if (counter.current === localCounter) {
          onSuccess(matrix);
        }
      })
      .finally(() => setMatrixLoading(false))
      .catch(onError);
  };
  return [matrixLoading, onMatrixChange];
};
